import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPage, goBack } from "../store/slices/pageSlice";
import Header from "../components/header/Header";
import Avatar from "../components/avatar/Avatar";
import CoinBadge from "../components/coin_badge/CoinBadge";
import getIcon from "../utils/Icon";
import usersIcon from '../assets/icon/users.png'
import cupIcon from '../assets/icon/cup.png'
import tokenApi from "../api/tokenApi";
import CrystalsApi from "../api/CrystalsApi";
import awardApi from "../api/awardApi";
import { pushBackHandler, popBackHandler } from "../utils/backHandler";

// Иконки для cryptoOnly позиций
import goldIcon from '../assets/icon/cryptoposition/gold.png';
import silverIcon from '../assets/icon/cryptoposition/silver.png';
import bronzeIcon from '../assets/icon/cryptoposition/bronze.png';
import royalIcon from '../assets/icon/cryptoposition/royal.png';
import emeraldIcon from '../assets/icon/cryptoposition/emerald.png';
import devineIcon from '../assets/icon/cryptoposition/devine.png';
import supremeIcon from '../assets/icon/cryptoposition/supreme.png';
import { getCrystalRarityTiers } from '../data/crystalRarityTiers';

const TIER_ICONS = {
    Bronze: bronzeIcon,
    Silver: silverIcon,
    Gold: goldIcon,
    Lucky: emeraldIcon,
    Royal: royalIcon,
    Nova: supremeIcon,
    Diamond: devineIcon,
};

const buildCryptoPositionIcons = (total) =>
    getCrystalRarityTiers(total).map((tier) => ({
        ...tier,
        icon: TIER_ICONS[tier.name],
    }));

const cryptoPositionIcons = {
    3: buildCryptoPositionIcons(3),
    5: buildCryptoPositionIcons(5),
    7: buildCryptoPositionIcons(7),
};

export default function CompletedPromoLayout() {
    const dispatch = useDispatch();
    const [isFullView, setIsFullView] = useState(false);
    const [tokenLogo, setTokenLogo] = useState('');
    const [crystals, setCrystals] = useState([]);
    const [awardsStats, setAwardsStats] = useState({ awards: [], positions: [] });
    const [awardsLoading, setAwardsLoading] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState(null); // null = All, number = position
    const [displayCount, setDisplayCount] = useState(10); // Пагинация — показывать по 10
    
    // Перехват Telegram BackButton при открытом full view
    const closeFullView = useCallback(() => setIsFullView(false), []);
    useEffect(() => {
        if (!isFullView) return;
        pushBackHandler(closeFullView);
        return () => popBackHandler(closeFullView);
    }, [isFullView, closeFullView]);

    // Получаем выбранное промо из Redux
    const promo = useSelector((state) => state.selectedPromo.promo);
    
    // Данные промоутера
    const promoter = promo?.promoters?.[0];
    const promoterName = promoter?.name || 'Unknown';
    const promoterUsername = promoter?.username || '@unknown';
    const promoterLogo = promoter?.logo ? `https://api.duotetra.quest${promoter.logo}` : null;
    
    // Статус промо
    const isLive = promo?.status === 'ACTIVE';
    const isCompleted = promo?.status === 'COMPLETED' || promo?.status === 'MANUALLY_COMPLETED';
    
    // Сумма и токен
    const amount = promo?.currentAmount || promo?.totalAmount || 0;
    const tokenType = promo?.tokenType;
    const promoType = promo?.promoType || 'crystalsAndCrypto';
    
    // Загружаем логотип токена
    useEffect(() => {
        if (tokenType) {
            tokenApi.lookupToken(tokenType, 't')
                .then(res => setTokenLogo(res?.logoPath || ''))
                .catch(() => setTokenLogo(''));
        }
    }, [tokenType]);
    
    // Загружаем кристаллы для позиций
    useEffect(() => {
        if (promoType === 'cryptoOnly') {
            setCrystals([]);
            return;
        }
        const posCount = promo?.category?.positions?.length;
        if (posCount) {
            // Создаем массив от 1 до длины массива позиций (как в DetailInfoTable)
            const positionsArray = Array.from({ length: posCount }, (_, i) => i + 1);
            console.log('[CompletedPromo] Запрос гемов по позициям:', positionsArray);
            CrystalsApi.getGemsByPositions(positionsArray)
                .then(res => {
                    console.log('[CompletedPromo] crystals:', res);
                    setCrystals(res || []);
                })
                .catch(() => setCrystals([]));
        }
    }, [promo?.category?.positions?.length, promoType]);
    
    // Загружаем статистику по наградам
    useEffect(() => {
        const promoId = promo?.id;
        if (!promoId || typeof promoId !== 'number') {
            setAwardsStats({ awards: [], positions: [] });
            return;
        }
        
        setAwardsLoading(true);
        awardApi.getPromoAwardsStats(promoId)
            .then(res => {
                console.log('[CompletedPromo] awards stats:', res);
                setAwardsStats(res || { awards: [], positions: [] });
            })
            .catch(err => {
                console.error('[CompletedPromo] failed to load awards stats:', err);
                setAwardsStats({ awards: [], positions: [] });
            })
            .finally(() => setAwardsLoading(false));
    }, [promo?.id]);

    const handleClose = () => {
        dispatch(goBack());
    };

    const PromoProfile = ()=><div className={'w-full flex flex-col items-center gap-6'}>
            <div className={'w-full flex flex-row items-center justify-between px-6'}>
                <div className={'flex flex-row items-center gap-2'}>
                    {promoterLogo ? (
                        <img 
                            src={promoterLogo} 
                            alt={promoterName}
                            className="w-[45px] h-[45px] rounded-full border border-white/10 object-cover"
                        />
                    ) : (
                        <Avatar width={45} height={45} label={'octo'} />
                    )}
                    <div className={'flex flex-col items-start'}>
                        <span className={'text-[15px] text-[#EAEAEA]'}>{promoterName}</span>
                        <span className={'text-[12px] text-[#959595]'}>{promoterUsername}</span>
                    </div>
                </div>
                {isLive && (
                    <div className={'px-4 h-[17px] rounded-xl bg-[#2FD159] text-[12px] text-black flex flex-row items-center justify-center'}>
                        live
                    </div>
                )}
                {isCompleted && (
                    <div className={'px-4 h-[17px] rounded-xl bg-[#959595] text-[12px] text-black flex flex-row items-center justify-center'}>
                        completed
                    </div>
                )}
            </div>
            <div className={'w-[238px] h-[42.5px]'}>
                <CoinBadge 
                    count={amount} 
                    token={tokenLogo ? `https://api.duotetra.quest/${tokenLogo}` : 'SUI'} 
                    isGiveaway={true}
                />
            </div>
    </div>

    // Количество позиций из данных промо (перед PositionItem чтобы был доступен внутри)
    const positions = promo?.category?.positions || [];
    const positionsCount = positions.length || 3;

    // Компонент одной позиции с возможностью смещения и размера
    // positionData: { percentage, values, fee_reward, total } из promo.category.positions
    // crystal: { frontImage, name } из CrystalsApi
    // positionIndex: индекс позиции (0-based) для cryptoOnly иконок
    // winnersCount: сколько людей уже выиграли (потом передадим)
    const PositionItem = ({ 
        position = 1, 
        offset = 0, 
        size = 'normal',
        positionData = null,
        crystal = null,
        positionIndex = 0,
        winnersCount = 0
    }) => {
        const isSmall = size === 'small';
        const isCryptoOnly = promoType === 'cryptoOnly';
        
        // Данные из позиции
        // winners — количество мест для победы
        // dropPerWinner — награда каждому победителю
        const reward = positionData?.dropPerWinner || 0;
        const maxWinners = positionData?.winners || 0;
        
        // Для cryptoOnly берём иконку из маппинга
        const cryptoIconData = isCryptoOnly 
            ? (cryptoPositionIcons[positionsCount] || cryptoPositionIcons[3])[positionIndex] 
            : null;
        
        // Иконка и название
        let displayIcon = null;
        let displayName = '';
        
        // Цвет римских цифр для cryptoOnly
        const positionColor = cryptoIconData?.color || '#EAEAEA';
        
        if (isCryptoOnly && cryptoIconData) {
            // cryptoOnly — используем иконки из cryptoposition
            displayIcon = cryptoIconData.icon;
            displayName = cryptoIconData.name;
        } else if (crystal?.frontImage) {
            // onlyCrystals или crystalsAndCrypto — используем кристалл
            displayIcon = `https://api.duotetra.quest/uploads/gems/${crystal.frontImage}`;
            displayName = crystal.name || '';
        } else {
            // Fallback
            displayIcon = getIcon('star');
            displayName = 'Reward';
        }
        
        const tokenIcon = tokenLogo ? `https://api.duotetra.quest/${tokenLogo}` : getIcon('sui');
        
        // Размеры иконок: для cryptoOnly увеличиваем в 2 раза
        const iconSize = isCryptoOnly 
            ? (isSmall ? 'w-[48px] h-[48px]' : 'w-[64px] h-[64px]')
            : (isSmall ? 'w-[24px] h-[24px]' : 'w-[32px] h-[32px]');
        
        // Размеры контейнера и фона остаются одинаковыми
        const containerSize = isSmall ? 'w-[80px] h-[115px]' : 'w-[104px] h-[135px]';
        const bgSize = isSmall ? 'w-[85px] h-[90px] bottom-[-12px]' : 'w-[110px] h-[110px] bottom-[-15px]';

        return (
            <div 
                className={`flex flex-col items-center relative ${containerSize}`} 
                style={{ marginTop: offset }}
            >
                {/* Градиентный фон - нижний слой */}
                <div 
                    className={`absolute rounded-[4px] z-0 ${bgSize}`} 
                    style={{ background: 'linear-gradient(to bottom, #353232, #000000)' }}
                />
                {/* Контент - верхний слой */}
                {/* Римские цифры только если НЕ cryptoOnly */}
                {!isCryptoOnly && (
                    <span className={`w-full flex flex-col items-center justify-center relative z-10 text-[#959595] ${isSmall ? 'text-[10px]' : 'text-[12px]'}`}>
                        {position}
                    </span>
                )}
                {/* Кристалл или иконка cryptoOnly */}
                <div className={`relative z-10 ${iconSize}`}>
                    <img src={displayIcon} className={'w-full h-full object-contain'} alt={displayName}/>
                    {/* Для cryptoOnly — римские цифры поверх иконки */}
                    {isCryptoOnly && (
                        <span 
                            className={`absolute inset-0 flex items-center justify-center font-bold ${isSmall ? 'text-[12px] -mt-6' : 'text-[16px] -mt-7'}`}
                            style={{ color: positionColor }}
                        >
                            {position}
                        </span>
                    )}
                </div>
                {/* Название */}
                <span className={`w-full flex flex-row items-center justify-center relative z-10 text-white ${isSmall ? 'text-[11px]' : 'text-[13px]'}`}>
                    {displayName}
                </span>
                {/* Награда в токене (показываем для cryptoOnly и crystalsAndCrypto, НЕ показываем для onlyCrystals) */}
                {promoType !== 'onlyCrystals' && (
                    <div className={'w-full flex flex-row items-center justify-center gap-1 relative z-10'}>
                        <img width={isSmall ? 12 : 16} height={isSmall ? 12 : 16} src={tokenIcon} className={'rounded-full'}/>
                        <span className={`text-[#DEC315] flex flex-row items-center justify-center ${isSmall ? 'text-[10px]' : 'text-[12px]'}`}>
                            {reward.toFixed(4)}
                        </span>
                    </div>
                )}
                {/* Счетчик победителей */}
                <div className={'w-full flex flex-row items-center justify-center relative z-10'}>
                    <span className={`text-white ${isSmall ? 'text-[10px]' : 'text-[12px]'}`}>{winnersCount}</span>
                    <span className={`text-[#959595] ${isSmall ? 'text-[10px]' : 'text-[12px]'}`}>/{maxWinners}</span>
                </div>
            </div>
        );
    };

    // Создаем маппинг гемов по position для быстрого доступа (как в DetailInfoTable)
    const gemsMap = {};
    crystals.forEach(gem => {
        if (gem.position) {
            gemsMap[gem.position] = gem;
        }
    });

    // Маппинг статистики по позициям (position 1-based)
    const positionStatsMap = {};
    awardsStats.positions?.forEach(ps => {
        if (ps.position) {
            positionStatsMap[ps.position] = ps;
        }
    });

    // Хелпер для получения данных позиции по индексу (0-based)
    // Для кристаллов берём в обратном порядке: positionsCount - index
    const getPositionProps = (index) => {
        const positionNumber = index + 1; // 1-based position
        const reversedPositionNumber = positionsCount - index;
        const crystal = gemsMap[reversedPositionNumber] || null;
        const posStats = positionStatsMap[positionNumber];
        return {
            positionData: positions[index] || null,
            crystal,
            positionIndex: index,
            winnersCount: posStats?.issued || 0, // Сколько уже выиграли
        };
    };

    const Positions = () => {
        // 3 позиции: 2-1-3 (1 выше по центру)
        if (positionsCount === 3) {
            return (
                <div className={'w-full flex flex-col items-center px-4'}>
                    <div className={'flex flex-row items-start justify-center gap-4'}>
                        <PositionItem position="II" offset={20} {...getPositionProps(1)} />
                        <PositionItem position="I" offset={0} {...getPositionProps(0)} />
                        <PositionItem position="III" offset={20} {...getPositionProps(2)} />
                    </div>
                </div>
            );
        }

        // 5 позиций: верх 2-1-3, низ 4-5
        if (positionsCount === 5) {
            return (
                <div className={'w-full flex flex-col items-center px-4 gap-8'}>
                    {/* Верхний ряд */}
                    <div className={'flex flex-row items-start justify-center gap-4'}>
                        <PositionItem position="II" offset={20} {...getPositionProps(1)} />
                        <PositionItem position="I" offset={0} {...getPositionProps(0)} />
                        <PositionItem position="III" offset={20} {...getPositionProps(2)} />
                    </div>
                    {/* Нижний ряд */}
                    <div className={'flex flex-row items-start justify-center gap-4'}>
                        <PositionItem position="IV" offset={0} {...getPositionProps(3)} />
                        <PositionItem position="V" offset={0} {...getPositionProps(4)} />
                    </div>
                </div>
            );
        }

        // 7 позиций: верх 2-1-3, низ 6-4-5-7 (4-5 выше, 6-7 ниже)
        if (positionsCount === 7) {
            return (
                <div className={'w-full flex flex-col items-center px-2 gap-6'}>
                    {/* Верхний ряд */}
                    <div className={'flex flex-row items-start justify-center gap-3'}>
                        <PositionItem position="II" offset={20} {...getPositionProps(1)} />
                        <PositionItem position="I" offset={0} {...getPositionProps(0)} />
                        <PositionItem position="III" offset={20} {...getPositionProps(2)} />
                    </div>
                    {/* Нижний ряд: 6-4-5-7 (уменьшенные) */}
                    <div className={'flex flex-row items-start justify-center gap-2'}>
                        <PositionItem position="VI" offset={16} size="small" {...getPositionProps(5)} />
                        <PositionItem position="IV" offset={0} size="small" {...getPositionProps(3)} />
                        <PositionItem position="V" offset={0} size="small" {...getPositionProps(4)} />
                        <PositionItem position="VII" offset={16} size="small" {...getPositionProps(6)} />
                    </div>
                </div>
            );
        }

        // По умолчанию 3 позиции
        return (
            <div className={'w-full flex flex-col items-center px-4'}>
                <div className={'flex flex-row items-start justify-center gap-4'}>
                    <PositionItem position="II" offset={20} {...getPositionProps(1)} />
                    <PositionItem position="I" offset={0} {...getPositionProps(0)} />
                    <PositionItem position="III" offset={20} {...getPositionProps(2)} />
                </div>
            </div>
        );
    }

    // Данные участников
    const maxParticipants = promo?.maxParticipantsCount || promo?.participantsCount || 0;
    const currentParticipants = promo?.participantsCount || 0;
    const prizePlaces = promo?.prizePlacesCount || 0;
    const nonWinners = Math.max(0, currentParticipants - prizePlaces);
    const participantPercent = maxParticipants > 0 ? Math.round((currentParticipants / maxParticipants) * 100) : 0;
    const winnersPercent = currentParticipants > 0 ? Math.round((prizePlaces / currentParticipants) * 100) : 0;
    const nonWinnersPercent = currentParticipants > 0 ? Math.round((nonWinners / currentParticipants) * 100) : 0;

    const ParticipantAndWinners = ()=><div className={'w-full px-3 mt-6'}>
    <div className={'flex flex-row px-4 py-2  items-center justify-between rounded-[4px] border border-[#DEC315]'}>
            <div className={'flex flex-col items-center gap-2'}>
                <span className={'text-[10px] text-[#D8D8D8]'}>players</span>
                <div className={'flex flex-row items-center justify-center gap-1'}>
                    <span className={'text-[12px] text-[#D8D8D8] font-semibold'}>{currentParticipants}/{maxParticipants}</span>
                    <img  width={10} height={10} src={usersIcon}/>
                    <span className={'text-[9px] text-[#D8D8D8] font-thin'}>{participantPercent}%</span>
                </div>
            </div>
            <div className={'flex flex-col items-center gap-2'}>
                <span className={'text-[10px] text-[#D8D8D8]'}>winners</span>
                <div className={'flex flex-row items-center justify-center gap-1'}>
                    <span className={'text-[12px] text-[#D8D8D8] font-semibold'}>{prizePlaces}</span>
                    <img  width={10} height={10} src={cupIcon}/>
                    <span className={'text-[9px] text-[#D8D8D8] font-thin'}>{winnersPercent}%</span>
                </div>
            </div>
            <div className={'flex flex-col items-center gap-2'}>
                <span className={'text-[10px] text-[#D8D8D8]'}>non-winners</span>
                <div className={'flex flex-row items-center justify-center gap-1'}>
                    <span className={'text-[12px] text-[#D8D8D8] font-semibold'}>{nonWinners}</span>
                    <span className={'text-[9px] text-[#D8D8D8] font-thin'}>{nonWinnersPercent}%</span>
                </div>
            </div>
    </div>
    </div>

    // Генерируем строки таблицы из реальных данных
    // Для каждой позиции рисуем столько строк, сколько мест (winners)
    const generateTableRows = () => {
        const rows = [];
        let rowId = 1;
        
        // Реальные данные победителей из API
        const winnersData = awardsStats.awards || [];
        
        // Для каждой позиции
        for (let posIndex = 0; posIndex < positionsCount; posIndex++) {
            const positionNumber = posIndex + 1;
            const posData = positions[posIndex];
            const totalSlots = posData?.winners || 0;
            const dropPerWinner = posData?.dropPerWinner || 0;
            
            // Получаем иконку для позиции
            const isCryptoOnly = promoType === 'cryptoOnly';
            let rankIcon = null;
            if (isCryptoOnly) {
                const cryptoIconData = (cryptoPositionIcons[positionsCount] || cryptoPositionIcons[3])[posIndex];
                rankIcon = cryptoIconData?.icon || null;
            } else {
                const reversedPositionNumber = positionsCount - posIndex;
                const gem = gemsMap[reversedPositionNumber];
                rankIcon = gem?.frontImage ? `https://api.duotetra.quest/uploads/gems/${gem.frontImage}` : null;
            }
            
            // Победители в этой позиции
            const winnersInPosition = winnersData.filter(a => a.position === positionNumber) || [];
            
            // Рисуем totalSlots строк для этой позиции
            for (let slotIndex = 0; slotIndex < totalSlots; slotIndex++) {
                const winner = winnersInPosition[slotIndex]; // Победитель для этого слота (если есть)
                
                if (winner) {
                    // Есть победитель
                    rows.push({
                        id: rowId++,
                        rankIcon,
                        position: positionNumber,
                        name: winner.user ? `${winner.user.firstName || ''} ${winner.user.lastName || ''}`.trim() : 'Unknown',
                        username: winner.user?.telegramId ? `@${winner.user.telegramId}` : '',
                        reward: winner.amount || dropPerWinner,
                        hasUser: true,
                    });
                } else {
                    // Пустой слот
                    rows.push({
                        id: rowId++,
                        rankIcon,
                        position: positionNumber,
                        name: null,
                        username: null,
                        reward: dropPerWinner,
                        hasUser: false,
                    });
                }
            }
        }
        
        return rows;
    };
    
    const tableRows = generateTableRows();

    // Строка пользователя в списке
    const UserRewardRow = ({ id, rankIcon, position, name, username, reward, hasUser }) => {
        const tokenIcon = tokenLogo ? `https://api.duotetra.quest/${tokenLogo}` : getIcon('sui');
        
        return (
            <div className={'w-full h-[52px] min-h-[52px] max-h-[52px] flex flex-row items-stretch border-b border-[#353232]'}>
                {/* # номер */}
                <div className={'w-[40px] flex items-center justify-center border-r border-[#353232]'}>
                    <span className={'text-[14px] text-[#959595]'}>{id}</span>
                </div>
                
                {/* Rank иконка */}
                <div className={'w-[50px] flex items-center justify-center border-r border-[#353232] overflow-hidden'}>
                    {rankIcon && <img className="w-[24px] h-[24px] max-w-[24px] max-h-[24px] object-contain" src={rankIcon} />}
                </div>
                
                {/* User name */}
                <div className={'flex-1 flex flex-row items-center gap-2 pl-3 overflow-hidden'}>
                    {hasUser && name ? (
                        <>
                            <img className={'w-[36px] h-[36px] min-w-[36px] rounded-full'} src={getIcon('octo')} />
                            <div className={'flex flex-col min-w-0'}>
                                <span className={'text-[14px] text-white font-medium truncate'}>{name}</span>
                                <span className={'text-[12px] text-[#959595] truncate'}>{username}</span>
                            </div>
                        </>
                    ) : (
                        <span className={'text-[14px] text-[#959595] italic'}>empty</span>
                    )}
                </div>
                
                {/* Reward */}
                {promoType !== 'onlyCrystals' && reward != null && (
                    <div className={'flex flex-row items-center gap-2 pr-2 flex-shrink-0'}>
                        <img className={'w-[24px] h-[24px] rounded-full'} src={tokenIcon} />
                        <span className={'text-[16px] text-[#DEC315] font-semibold'}>{Number(reward).toFixed(4)}</span>
                    </div>
                )}
            </div>
        );
    };

    // Фильтрация строк таблицы
    const filteredTableRows = selectedFilter 
        ? tableRows.filter(row => row.position === selectedFilter)
        : tableRows;
    
    // Строки для отображения (с пагинацией)
    const displayedRows = filteredTableRows.slice(0, displayCount);
    const hasMore = displayCount < filteredTableRows.length;
    
    // Сброс пагинации при смене фильтра
    useEffect(() => {
        setDisplayCount(10);
    }, [selectedFilter]);
    
    // Обработчик скролла для подгрузки
    // Ref для сохранения позиции скролла
    const scrollRef = React.useRef(null);
    const fullViewScrollRef = React.useRef(null);
    const scrollPosRef = React.useRef(0);
    
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        scrollPosRef.current = scrollTop; // Сохраняем позицию
        // Если доскроллили до конца (с запасом 50px) и есть ещё данные
        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore) {
            setDisplayCount(prev => prev + 10);
        }
    };
    
    // Восстанавливаем позицию скролла после подгрузки
    useEffect(() => {
        if (scrollRef.current && scrollPosRef.current > 0) {
            scrollRef.current.scrollTop = scrollPosRef.current;
        }
    }, [displayCount]);
    
    // Автозаполнение в fullview — подгружаем пока не появится скролл
    useEffect(() => {
        if (!isFullView || !hasMore) return;
        
        const checkAndFill = () => {
            const el = fullViewScrollRef.current;
            if (!el) return;
            
            // Если контент не заполняет контейнер (нет скролла) — подгружаем ещё
            if (el.scrollHeight <= el.clientHeight && hasMore) {
                setDisplayCount(prev => prev + 10);
            }
        };
        
        // Небольшая задержка чтобы DOM обновился
        const timer = setTimeout(checkAndFill, 50);
        return () => clearTimeout(timer);
    }, [isFullView, displayCount, hasMore]);

    // Контент таблицы (используется и в обычном, и в fullview режиме)
    const TableContent = ({ isFullScreen = false }) => (
        <div className={`w-full border border-[#353232] rounded-lg flex-1 flex flex-col ${isFullScreen ? 'overflow-hidden' : 'overflow-hidden min-h-0'}`}>
            {/* Заголовок таблицы */}
            <div className={'w-full flex flex-row items-stretch border-b border-[#353232] bg-[#1a1a1a] flex-shrink-0'}>
                <div className={'w-[40px] flex items-center justify-center border-r border-[#353232] py-2'}>
                    <span className={'text-[12px] text-[#959595]'}>#</span>
                </div>
                <div className={'w-[50px] flex items-center justify-center border-r border-[#353232] py-2'}>
                    <span className={'text-[12px] text-[#959595]'}>Rank</span>
                </div>
                <div className={'flex-1 flex items-center pl-3 py-2'}>
                    <span className={'text-[12px] text-[#959595]'}>User name</span>
                </div>
                {/* Фильтр по позициям */}
                <div className={'flex flex-row items-center gap-[2px] pr-1 py-1 overflow-x-auto hide-scrollbar'}>
                    {filterIcons.map((icon, idx) => (
                        <div 
                            key={idx}
                            onClick={() => setSelectedFilter(selectedFilter === icon.position ? null : icon.position)}
                            className={`w-[22px] h-[22px] min-w-[22px] rounded flex items-center justify-center cursor-pointer transition-all ${
                                selectedFilter === icon.position ? 'bg-white/30 ring-1 ring-white/50' : 'hover:bg-white/20'
                            }`}
                        >
                            <img 
                                className="w-[16px] h-[16px] object-contain"
                                src={icon.src} 
                                alt={`position-${icon.position}`}
                            />
                        </div>
                    ))}
                    {/* Кнопка All (сброс фильтра) */}
                    <div 
                        onClick={() => setSelectedFilter(null)}
                        className={`w-[22px] h-[22px] min-w-[22px] rounded flex items-center justify-center cursor-pointer transition-all ${
                            selectedFilter === null ? 'bg-white/30 ring-1 ring-white/50' : 'bg-white/10 hover:bg-white/20'
                        }`}
                    >
                        <img className="w-[14px] h-[14px] rounded-full" src={getIcon('octo')} />
                    </div>
                </div>
            </div>
            
            {/* Список пользователей */}
            <div 
                ref={isFullScreen ? fullViewScrollRef : scrollRef}
                className={`w-full flex flex-col overflow-y-auto flex-1 min-h-0`}
                onScroll={handleScroll}
            >
                {awardsLoading ? (
                    <div className="py-4 text-center text-[#959595] text-sm">Loading...</div>
                ) : displayedRows.length === 0 ? (
                    <div className="py-4 text-center text-[#959595] text-sm">No winners yet</div>
                ) : (
                    <>
                        {displayedRows.map((row) => (
                            <UserRewardRow key={row.id} {...row} />
                        ))}
                        {hasMore && (
                            <div className="py-2 text-center text-[#959595] text-[11px]">
                                Scroll for more...
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // Полноэкранный режим таблицы
    const FullViewTable = () => (
        <div className={'fixed inset-0 z-50 bg-black flex flex-col'}>
            {/* Header */}
            <div className="pt-[24px] pb-[10px] flex-shrink-0 px-3">
                <div className={'w-full flex flex-row items-center justify-between'}>
                    <button 
                        onClick={() => setIsFullView(false)}
                        className={'text-[14px] text-[#5BC2E7]'}
                    >
                        ← Back
                    </button>
                    <span className={'text-[16px] text-white font-semibold'}>Promo reward distribution</span>
                    <div className={'w-[50px]'} /> {/* Spacer для центрирования */}
                </div>
            </div>
            
            {/* Таблица на весь экран */}
            <div className={'flex-1 flex flex-col px-3 pb-6 overflow-hidden'}>
                <TableContent isFullScreen={true} />
            </div>
        </div>
    );

    // Генерируем иконки фильтра из позиций
    const getFilterIcons = () => {
        const isCryptoOnly = promoType === 'cryptoOnly';
        const icons = [];
        
        for (let i = 0; i < positionsCount; i++) {
            if (isCryptoOnly) {
                // Для cryptoOnly — локальные иконки
                const cryptoIconData = (cryptoPositionIcons[positionsCount] || cryptoPositionIcons[3])[i];
                if (cryptoIconData) {
                    icons.push({
                        src: cryptoIconData.icon,
                        isLocal: true,
                        position: i + 1,
                    });
                }
            } else {
                // Для crystalsAndCrypto / onlyCrystals — иконки из API
                const reversedPositionNumber = positionsCount - i;
                const gem = gemsMap[reversedPositionNumber];
                if (gem?.frontImage) {
                    icons.push({
                        src: `https://api.duotetra.quest/uploads/gems/${gem.frontImage}`,
                        isLocal: false,
                        position: i + 1,
                    });
                }
            }
        }
        
        return icons;
    };
    
    const filterIcons = getFilterIcons();

    const UsersRewardList = () => (
        <div className={'w-full flex flex-col flex-1 min-h-0 px-3 gap-2 mt-4'}>
            {/* Заголовок */}
            <div className={'w-full flex flex-row items-center justify-between flex-shrink-0'}>
                <span className={'text-[14px] text-[#D8D8D8]'}>Promo reward distribution</span>
                <button 
                    onClick={() => setIsFullView(true)}
                    className={'px-2 py-1 rounded-md bg-[#353232] text-[11px] text-[#5BC2E7] hover:bg-[#454545] transition-colors'}
                >
                    Full view
                </button>
            </div>
            
            <TableContent />
        </div>
    )

    return (
        <>
            {/* Full View модальное окно */}
            {isFullView && <FullViewTable />}
            
            <div className={'w-full h-full flex flex-col overflow-hidden'}>
                <div className="pt-[24px] pb-[10px] flex-shrink-0">
                    <Header
                        leftButtonText="Close"
                        centerTitle="Promo By"
                        onLeftClick={handleClose}
                        useBackIcon={false}
                    />
                </div>
                {/* Верхняя часть: скроллится, но не растягивается */}
                <div className={'flex-shrink-0 overflow-y-auto'} style={{maxHeight: '50%'}}>
                    <PromoProfile/>
                    <Positions/>
                    <ParticipantAndWinners/>
                </div>
                {/* Таблица: занимает всё оставшееся место */}
                <div className={'flex-1 flex flex-col min-h-0'}>
                    <UsersRewardList/>
                </div>
            </div>
        </>
    );
}
